"use client";

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { Bell, BellOff, Share, Plus, TriangleAlert, Check } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import { cn } from "@/lib/utils";
import {
  leesGesteldheid,
  abonneerSoorten,
  soortenMomentopname,
  soortenServerMomentopname,
  bewaarSoorten,
  zetAan,
  zetUit,
  werkBij,
  type Gesteldheid,
  type Soort,
} from "@/lib/meldingen-client";

// De ene knop die ertoe doet, en daaronder waar je bericht over krijgt.
//
// Pushmeldingen falen op een stuk of vijf manieren die de gebruiker niet kan
// zien: geen service worker, geen toestemming, op iPhone niet vanaf het
// beginscherm. Dit scherm zegt bij elk van die gevallen wat er aan de hand is
// en wat je eraan kunt doen - een schuifje dat stilletjes niets doet is erger
// dan geen schuifje.

// Wat hier staat moet kloppen met wat de uitkijk werkelijk stuurt
// (meldingen-uitkijk.ts). Tot oktober 2026 stond hier "Wedstrijd begint" terwijl
// er nooit zo'n bericht uitging, en "afgelasting" terwijl de uitkijk een
// afgelaste wedstrijd niet eens ziet.
const SOORTEN: { soort: Soort; label: string; uitleg: string }[] = [
  {
    soort: "mijnTeams",
    label: "Aan de beurt",
    uitleg: "Als een team dat je volgt zo moet trekken, en als een klasse die je volgt begint",
  },
  {
    soort: "uitslag",
    label: "Uitslagen",
    uitleg: "De afstand van je teams, en de top drie als een klasse die je volgt klaar is",
  },
  {
    soort: "wedstrijddag",
    label: "Wedstrijddag",
    uitleg: "'s Ochtends, als er een wedstrijd is die je volgt of waar je teams rijden",
  },
  {
    soort: "uitstel",
    label: "Uitstel",
    uitleg: "Als het programma verschuift, bijvoorbeeld door regen",
  },
];

export function MeldingenInstellingen() {
  // Teams, wedstrijden en klassen: alle drie leveren ander bericht op. Wat
  // precies staat bovenaan meldingen-uitkijk.ts.
  const { favorieten, geladen } = useFavorieten();
  const [gesteldheid, setGesteldheid] = useState<Gesteldheid>({ staat: "laden" });
  // De gekozen soorten staan in localStorage; dat is een externe bron, dus geen
  // state die na het renderen alsnog wordt gezet.
  const soorten = useSyncExternalStore(
    abonneerSoorten,
    soortenMomentopname,
    soortenServerMomentopname,
  );
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  // Wat dit toestel kan is alleen asynchroon te achterhalen; die uitkomst komt
  // dus wel via state binnen, maar in een callback en niet in de effectbody.
  useEffect(() => {
    let levend = true;
    leesGesteldheid().then((g) => {
      if (levend) setGesteldheid(g);
    });
    return () => {
      levend = false;
    };
  }, []);

  // Volg je een team erbij of af, dan moet de server dat weten - anders krijg
  // je bericht over een team dat je niet meer volgt.
  const aan = gesteldheid.staat === "aan";
  useEffect(() => {
    if (!aan || !geladen) return;
    werkBij(favorieten, soorten);
  }, [aan, geladen, favorieten, soorten]);

  const wissel = useCallback(async () => {
    setFout(null);
    setBezig(true);
    try {
      if (gesteldheid.staat === "aan") {
        setGesteldheid(await zetUit());
      } else {
        const uitkomst = await zetAan(favorieten, soorten);
        setGesteldheid(uitkomst.gesteldheid);
        if (uitkomst.fout) setFout(uitkomst.fout);
      }
    } catch {
      setFout("Er ging iets mis bij het aanzetten. Probeer het zo nog eens.");
    } finally {
      setBezig(false);
    }
  }, [gesteldheid.staat, favorieten, soorten]);

  function wisselSoort(s: Soort) {
    bewaarSoorten(soorten.includes(s) ? soorten.filter((x) => x !== s) : [...soorten, s]);
  }

  if (gesteldheid.staat === "laden") {
    return <p className="px-4 py-8 text-sm text-muted-foreground">Even kijken wat dit toestel kan…</p>;
  }

  if (gesteldheid.staat === "ios-installeren") {
    return (
      <Uitleg titel="Zet de app eerst op je beginscherm">
        <p>
          Op een iPhone of iPad geeft Apple alleen meldingen aan apps die op het beginscherm staan.
          Dat kost twee tikken:
        </p>
        <ol className="mt-3 space-y-2">
          <li className="flex items-center gap-2">
            <Share className="size-4 shrink-0 text-primary" aria-hidden />
            Tik onderin op Deel
          </li>
          <li className="flex items-center gap-2">
            <Plus className="size-4 shrink-0 text-primary" aria-hidden />
            Kies &ldquo;Zet op beginscherm&rdquo;
          </li>
        </ol>
        <p className="mt-3">Open de app daarna vanaf het beginscherm en kom hier terug.</p>
      </Uitleg>
    );
  }

  if (gesteldheid.staat === "kan-niet") {
    return <Uitleg titel="Meldingen kunnen hier niet">{gesteldheid.reden}</Uitleg>;
  }

  if (gesteldheid.staat === "geweigerd") {
    return (
      <Uitleg titel="Meldingen staan geblokkeerd">
        Je hebt meldingen voor deze site eerder geweigerd. Dat kan de app niet zelf terugdraaien:
        zet het aan bij de site-instellingen van je browser en kom hier terug.
      </Uitleg>
    );
  }

  return (
    <div className="space-y-6 px-4">
      <button
        type="button"
        onClick={wissel}
        disabled={bezig}
        className={cn(
          "flex w-full items-center gap-3 rounded-xl border-2 px-4 py-4 text-left transition-colors disabled:opacity-60",
          aan ? "border-primary bg-primary/10" : "border-border bg-card",
        )}
      >
        <span
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-full",
            aan ? "bg-primary text-primary-foreground" : "bg-muted text-primary",
          )}
        >
          {aan ? <Bell className="size-5" /> : <BellOff className="size-5" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-lg font-black uppercase tracking-wide text-white">
            {aan ? "Meldingen staan aan" : "Meldingen aanzetten"}
          </span>
          <span className="block text-sm text-soft-foreground">
            {bezig
              ? "Bezig…"
              : aan
                ? "Tik om ze weer uit te zetten"
                : "Je browser vraagt je zo om toestemming"}
          </span>
        </span>
      </button>

      {fout && (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-white">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          {fout}
        </p>
      )}

      <div>
        <h2 className="mb-1 font-display text-sm font-bold uppercase tracking-wide text-muted-foreground">
          Waarover
        </h2>
        <p className="mb-3 text-xs text-muted-foreground">
          {aan
            ? "Wijzigingen gelden meteen."
            : "Je keuze wordt bewaard en gaat gelden zodra je meldingen aanzet."}
        </p>
        <ul className="space-y-2">
          {SOORTEN.map((s) => {
            const gekozen = soorten.includes(s.soort);
            return (
              <li key={s.soort}>
                <button
                  type="button"
                  onClick={() => wisselSoort(s.soort)}
                  aria-pressed={gekozen}
                  className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left transition-colors active:bg-white/5"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-white">{s.label}</span>
                    <span className="block text-xs text-muted-foreground">{s.uitleg}</span>
                  </span>
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors",
                      gekozen ? "border-primary bg-primary text-primary-foreground" : "border-white/25",
                    )}
                  >
                    {gekozen && <Check className="size-4" strokeWidth={3} />}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Zonder favorieten zijn de twee interessantste soorten leeg. Beter dat
          hier zeggen dan iemand laten wachten op bericht dat nooit komt. */}
      {geladen && favorieten.length === 0 && (
        <p className="rounded-lg border border-border bg-muted px-3 py-2.5 text-sm text-soft-foreground">
          Je volgt nog niets. Tik de ster aan bij een team, een wedstrijd of een klasse, dan krijg
          je er bericht over. Tot die tijd hoor je alleen van uitstel en wedstrijddagen, en dan van
          elke wedstrijd.{" "}
          <Link href="/zoeken" className="font-semibold text-primary underline">
            Zoeken
          </Link>
        </p>
      )}
    </div>
  );
}

function Uitleg({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <div className="mx-4 rounded-xl border border-border bg-card px-4 py-4">
      <p className="mb-2 flex items-center gap-2 font-display text-base font-bold uppercase tracking-wide text-white">
        <BellOff className="size-5 shrink-0 text-primary" aria-hidden />
        {titel}
      </p>
      <div className="text-sm text-soft-foreground">{children}</div>
    </div>
  );
}
